import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { AuthProvider } from "@refinedev/core";

export const TOKEN_KEY = "refine-auth";
export const TOKEN_TYPE_KEY = "refine-auth-type";

export const axiosInstance = axios.create();

axiosInstance.interceptors.request.use(
    (request: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem(TOKEN_KEY);
      const token_type = localStorage.getItem(TOKEN_TYPE_KEY);
      request.headers["Authorization"] = `${token_type} ${token}`;
      return request;
    },
);

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

// The backend keeps its login endpoints: /api/v1/login/access-token (username + password)
// and /api/v1/login/test-token (validates the stored bearer token).
export const authProvider: AuthProvider = {
  login: async ({ username, email, password }) => {
    try {
      const response = await axios.post("/api/v1/login/access-token", {
        username: username ?? email,
        password: password,
      });
      localStorage.setItem(TOKEN_KEY, response.data.access_token);
      localStorage.setItem(TOKEN_TYPE_KEY, response.data.token_type);
      return { success: true, redirectTo: "/" };
    } catch (error) {
      return {
        success: false,
        error: { name: "Login failed", message: errorMessage(error, "Unexpected error") },
      };
    }
  },
  logout: async () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_TYPE_KEY);
    return { success: true, redirectTo: "/login" };
  },
  onError: async (error) => {
    const status = error?.status ?? error?.response?.status;
    if (status === 403) {
      return { logout: true, redirectTo: "/login", error };
    }
    return { error };
  },
  check: async () => {
    try {
      await axiosInstance.post("/api/v1/login/test-token");
      return { authenticated: true };
    } catch {
      return { authenticated: false, redirectTo: "/login", logout: true };
    }
  },
  getPermissions: async () => null,
  getIdentity: async () => {
    const response = await axiosInstance.post("/api/v1/login/test-token");
    return response.data;
  },
};
